import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Clock, Wrench } from "lucide-react";
import { caseStudies } from "@/data/caseStudies";
import { MetricTiles } from "@/components/MetricTiles";

/**
 * Homepage case study switcher. Content comes from src/data/caseStudies.ts —
 * anonymous business types, conservative outcomes, no unverified figures.
 */
export function CaseStudyTabs() {
  const [activeSlug, setActiveSlug] = useState(caseStudies[0].slug);
  const study = caseStudies.find((s) => s.slug === activeSlug) ?? caseStudies[0];
  const activeIndex = caseStudies.findIndex((s) => s.slug === study.slug);

  const step = (dir: 1 | -1) => {
    const next = (activeIndex + dir + caseStudies.length) % caseStudies.length;
    setActiveSlug(caseStudies[next].slug);
  };

  return (
    <section
      className="section-padding bg-background relative overflow-hidden"
      aria-labelledby="case-studies-heading"
    >
      <div className="absolute inset-0 grid-pattern opacity-25" />

      <div className="container-wide relative z-10">
        <div className="max-w-3xl mb-9 sm:mb-12">
          <span className="section-badge mb-4">Case Studies</span>
          <h2
            id="case-studies-heading"
            className="heading-section text-foreground mt-4 mb-4 text-balance"
          >
            Real work for{" "}
            <span className="heading-accent">trade and service businesses</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-[1.7] prose-cap">
            Examples of how we've helped Australian trade and service businesses strengthen
            their websites, Google presence, reviews and lead follow-up. Client details are
            kept private where permission to publish identifying information hasn't been given.
          </p>
        </div>

        {/* Mobile stepper */}
        <div className="sm:hidden mb-6">
          <div className="flex items-center justify-between gap-2 bg-card border border-border/60 rounded-full p-1.5 shadow-sm">
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Previous case study"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-muted text-foreground active:scale-95 transition-transform"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 text-center min-w-0 px-2">
              <div className="text-[0.7rem] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                {activeIndex + 1} / {caseStudies.length}
              </div>
              <div className="text-sm font-bold text-foreground truncate">{study.business}</div>
            </div>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Next case study"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground active:scale-95 transition-transform"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Desktop tabs */}
        <div className="hidden sm:flex flex-wrap gap-2 mb-6" role="tablist">
          {caseStudies.map((s) => {
            const isActive = s.slug === study.slug;
            return (
              <button
                key={s.slug}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveSlug(s.slug)}
                className={`min-h-[48px] px-5 rounded-full text-sm font-semibold border transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-muted-foreground border-border/60 hover:text-foreground hover:border-primary/30"
                }`}
              >
                {s.business}
              </button>
            );
          })}
        </div>

        {/* Active study */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-card overflow-hidden">
          <div className="p-6 sm:p-8 lg:p-10">
            <div className="flex flex-wrap gap-2 mb-5">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-[0.8125rem] font-medium text-muted-foreground">
                <Wrench className="w-3.5 h-3.5" />
                {study.service}
              </span>
              {study.measurementPeriod && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-[0.8125rem] font-medium text-muted-foreground">
                  <Clock className="w-3.5 h-3.5" />
                  {study.measurementPeriod}
                </span>
              )}
            </div>

            <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3 text-balance">
              {study.headline}
            </h3>
            <p className="text-base text-muted-foreground leading-[1.7] prose-cap mb-6">
              {study.startingPoint[0]}
            </p>

            <MetricTiles
              metrics={study.metrics}
              size="md"
              className="sm:grid-cols-3 items-stretch mb-6"
            />

            <div className="rounded-xl border border-border/60 bg-background p-5 mb-6">
              <div className="text-[0.6875rem] font-bold uppercase tracking-[0.14em] text-muted-foreground mb-2">
                What we changed
              </div>
              <p className="text-[0.9375rem] text-muted-foreground leading-[1.65] prose-cap">
                {study.workCompleted[0]}
              </p>
            </div>

            {study.limitations[0] && (
              <p className="text-[0.8125rem] text-muted-foreground/80 leading-[1.6] prose-cap mb-6">
                {study.limitations[0]}
              </p>
            )}


            <Link
              to={`/case-studies/${study.slug}`}
              className="inline-flex items-center gap-2 min-h-[48px] text-sm font-bold text-primary hover:underline underline-offset-4"
            >
              View Case Study
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="mt-8">
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 min-h-[48px] text-sm font-bold text-foreground hover:text-primary transition-colors"
          >
            See all case studies
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
